import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { HOTMART_URL } from './UpgradeCTA';
import { trackButtonClick } from '../analytics';
import { CheckCircleOutlined } from '../icons';
import './PlansContent.css';

// TODO: substituir pelos links reais de checkout assim que os produtos
// trimestral e anual forem criados na Hotmart (hoje só existe o mensal, ligado
// a HOTMART_URL). Até lá esses dois botões continuam apontando pra um link
// que ainda não existe de fato.
const HOTMART_URL_TRIMESTRAL = 'https://pay.cakto.com.br/poemijy';
const HOTMART_URL_ANNUAL = 'https://pay.cakto.com.br/ngaefvv';

const BENEFITS = [
  'Todos os simulados liberados',
  'Correção comentada de cada questão',
  'Progresso do edital por matéria/tópico',
  'Cronograma de estudos personalizado',
  'Material completo por matéria',
  'Diário de treino físico (TAF)',
  'Ranking e estatísticas avançadas'
];

const formatPrice = (cents) => (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Grade de planos em si (hero + cards + rodapé), sem o chassi de página nem
// de modal — front/src/dashboard/Plans.js usa isso numa página cheia
// (/planos) e front/src/components/PlansModal.js usa isso dentro de um
// modal, pros gates contextuais de plano free (simulado, diário de estudos,
// diário de treinos, questões livres). heroTitle/heroSubtitle/trackingPrefix
// deixam a copy e os eventos de analytics contextuais por ponto de entrada.
function PlansContent({
  heroBadge = 'PREPARATE PREMIUM',
  heroTitle = 'Escolha seu plano e libere tudo',
  heroSubtitle = 'Simulados completos, correção comentada, cronograma, progresso do edital, materiais e treino físico — sem limites.',
  trackingPrefix = 'plans'
}) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');

  useEffect(() => {
    axios.get(`${apiUrl}plans`)
      .then(({ data }) => {
        setPlan(data.plan);
      })
      .catch((error) => {
        console.error('Erro ao buscar os planos:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiUrl]);

  if (loading) {
    return <div className="plans-loading">Carregando planos...</div>;
  }

  if (!plan) {
    return <div className="plans-loading">Não foi possível carregar os planos agora. Tente novamente mais tarde.</div>;
  }

  const monthlyCents = plan.price_monthly_cents;
  const trimestralCents = plan.price_trimestral_cents;
  const annualCents = plan.price_annual_cents;
  const annualPerMonthCents = annualCents != null ? Math.round(annualCents / 12) : null;
  const trimestralPerMonthCents = trimestralCents != null ? Math.round(trimestralCents / 3) : null;
  const annualSavingsPercent = monthlyCents && annualPerMonthCents != null
    ? Math.round((1 - annualPerMonthCents / monthlyCents) * 100)
    : null;
  const trimestralSavingsPercent = monthlyCents && trimestralPerMonthCents != null
    ? Math.round((1 - trimestralPerMonthCents / monthlyCents) * 100)
    : null;

  return (
    <>
      <div className="plans-hero">
        <span className="plans-hero-badge">{heroBadge}</span>
        <h1 className="plans-hero-title">{heroTitle}</h1>
        <p className="plans-hero-subtitle">{heroSubtitle}</p>
      </div>

      <div className="plans-grid">
        {monthlyCents != null && (
          <div className="plan-card">
            <span className="plan-card-name">Mensal</span>
            <div className="plan-card-price">
              <span className="plan-card-price-value">R$ {formatPrice(monthlyCents)}</span>
              <span className="plan-card-price-period">/mês</span>
            </div>
            <span className="plan-card-note">Cobrança mensal, cancele quando quiser</span>
            <a
              className="plan-card-cta"
              href={HOTMART_URL}
              onClick={() => trackButtonClick(`${trackingPrefix}_assinar_mensal`)}
            >
              Assinar mensal
            </a>
          </div>
        )}

        {annualCents != null && (
          <div className="plan-card plan-card--featured">
            {annualSavingsPercent != null && annualSavingsPercent > 0 && (
              <span className="plan-featured-badge">MAIS VANTAJOSO · ECONOMIZE {annualSavingsPercent}%</span>
            )}
            <span className="plan-card-name">Anual</span>
            <div className="plan-card-price">
              <span className="plan-card-price-value">R$ {formatPrice(annualPerMonthCents)}</span>
              <span className="plan-card-price-period">/mês</span>
            </div>
            <span className="plan-card-note">R$ {formatPrice(annualCents)} cobrados uma vez por ano</span>

            <div className="plan-card-benefits">
              {BENEFITS.map((benefit) => (
                <div key={benefit} className="plan-card-benefit">
                  <CheckCircleOutlined style={{ fontSize: 16 }} />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <a
              className="plan-card-cta plan-card-cta--featured"
              href={HOTMART_URL_ANNUAL}
              onClick={() => trackButtonClick(`${trackingPrefix}_assinar_anual`)}
            >
              Quero o plano anual
            </a>
          </div>
        )}

        {trimestralCents != null && (
          <div className="plan-card">
            <span className="plan-card-name">Trimestral</span>
            <div className="plan-card-price">
              <span className="plan-card-price-value">R$ {formatPrice(trimestralPerMonthCents)}</span>
              <span className="plan-card-price-period">/mês</span>
            </div>
            <span className="plan-card-note">
              R$ {formatPrice(trimestralCents)} cobrados a cada 3 meses
              {trimestralSavingsPercent != null && trimestralSavingsPercent > 0 && ` · economize ~${trimestralSavingsPercent}%`}
            </span>
            <a
              className="plan-card-cta"
              href={HOTMART_URL_TRIMESTRAL}
              onClick={() => trackButtonClick(`${trackingPrefix}_assinar_trimestral`)}
            >
              Assinar trimestral
            </a>
          </div>
        )}
      </div>

      <span className="plans-footer-note">
        Todos os planos incluem os mesmos recursos — a diferença é só o valor por mês. Cancele quando quiser.
      </span>
    </>
  );
}

export default PlansContent;
